/**
 * `client.ts` — the ONLY module in the Frontend that constructs an
 * Axios instance. Every other file under `frontend/src/api/` and every
 * hook / component in the app MUST route its HTTP traffic through the
 * shared instance exported from here.
 *
 * This restriction is enforced two ways:
 *
 * 1. ESLint: `frontend/.eslintrc.cjs` declares a `no-restricted-imports`
 *    rule against `axios` (and `axios/*`) for every file under `src/**`
 *    EXCEPT this one (Req 4.5).
 * 2. Code review: this file is a protected extensibility seam (Req 16.6).
 *    New features MUST NOT edit it to add route- or resource-specific
 *    logic. Per-resource helpers live in sibling modules
 *    (`api/pages.ts`, `api/posts.ts`, `api/menus.ts`, …) that consume
 *    the shared `apiClient` exported below.
 *
 * Responsibilities:
 * - Instantiate a single shared Axios instance pointed at the headless
 *   WordPress REST API via `VITE_CMS_BASE_URL` (Req 4.3).
 * - Apply a 10-second request timeout so slow or dead backends surface
 *   as `status === 0` `ApiError`s instead of hanging routes (Req 17.1).
 * - Convert every non-2xx response and every transport-level failure
 *   into an `ApiError` via the shared response interceptor, so callers
 *   only ever have to reason about one rejection type (Req 4.4).
 *
 * Requirements: 4.3, 4.5, 16.6, 17.1.
 */

// `axios` may be imported here and ONLY here (see docblock above).
import axios from 'axios';
import type {
  AxiosError,
  AxiosInstance,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';

import { ApiError } from './errors';

/**
 * Read `VITE_CMS_BASE_URL` from the Vite env. This is the base URL of
 * the headless WordPress REST API (no trailing slash). When it is
 * missing or empty we throw immediately at module load time — every
 * other module in `src/api/` depends on this being valid, so failing
 * fast here is safer than shipping a broken client that produces
 * confusing 404s at runtime (spirit of Req 12.7 applied to the
 * Frontend bootstrap).
 */
const rawBaseUrl: unknown = import.meta.env.VITE_CMS_BASE_URL;
if (typeof rawBaseUrl !== 'string' || rawBaseUrl.trim().length === 0) {
  throw new Error(
    "Missing required environment variable 'VITE_CMS_BASE_URL'. " +
      'Set it in `frontend/.env` (see `frontend/.env.example`) to the ' +
      'base URL of the headless WordPress REST API before starting the ' +
      'app — for example `http://localhost:8080` in local dev or ' +
      '`https://cms.example.com` in production.',
  );
}

const baseURL: string = rawBaseUrl.trim();

/**
 * Shared Axios instance. Do NOT construct additional Axios instances
 * elsewhere in the Frontend — import this one instead.
 */
export const apiClient: AxiosInstance = axios.create({
  baseURL,
  timeout: 10_000, // 10 seconds (Req 17.1).
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
});

/**
 * REQUEST interceptor.
 *
 * In dev builds only, log outgoing requests via `console.debug` so
 * network activity is visible in the browser console without touching
 * production logs. Otherwise the config is returned unchanged.
 */
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
    if (import.meta.env.DEV) {
      const method = (config.method ?? 'get').toUpperCase();
      const url = config.url ?? '';
      // eslint-disable-next-line no-console
      console.debug(`[api] \u2192 ${method} ${url}`);
    }
    return config;
  },
);

/**
 * RESPONSE interceptor.
 *
 * 2xx responses pass through unchanged. Everything else — HTTP errors,
 * timeouts, DNS / socket failures — is converted to an `ApiError` by
 * `ApiError.fromAxios`, so downstream callers (TanStack Query hooks,
 * route components) only ever have to handle one rejection type
 * (Req 4.4). Timeouts and transport failures surface with
 * `status === 0` (Req 17.1).
 */
apiClient.interceptors.response.use(
  (response: AxiosResponse): AxiosResponse => response,
  (err: AxiosError): Promise<never> => {
    const requestUrl = err.config?.url ?? 'unknown';
    const apiError = ApiError.fromAxios(err, requestUrl);
    return Promise.reject(apiError);
  },
);

/**
 * Return the base URL the shared client was configured with.
 *
 * Useful for tests (asserting the client is wired to the expected
 * origin), diagnostics (surfacing the effective CMS URL in an admin /
 * debug panel), and the occasional URL-composition helper that needs
 * to reconstruct an absolute URL from a relative Axios `config.url`.
 */
export function getBaseUrl(): string {
  return baseURL;
}

export default apiClient;
